/**
 * Der anbieterneutrale Vertrag — die eigentliche Zusage von `IAM-001`.
 *
 * ADR-012 hält fest, dass Keycloak der erste Kandidat für einen leichteren
 * Ersatz ist, falls der Speicherbedarf je Kundenstack zum Engpass wird, und
 * macht diesen Vertrag deshalb zur Pflicht. Er hält nur, wenn hier
 * ausschließlich das steht, was sich zwischen Anbietern tatsächlich
 * unterscheidet.
 *
 * Nach dem Vergleich aus `POC-IAM-001` sind das drei Dinge:
 *
 *   1. **wo die Rollen stehen** — Anspruchsname und Form,
 *   2. **woran ein Dienstkonto zu erkennen ist** — es gibt kein OIDC-Feld dafür,
 *   3. **wie weit eine Abmeldung reicht** — nicht jeder Anbieter kann sie
 *      an die Anwendungen weitergeben.
 *
 * Alles andere — Ausstellerprüfung, Laufzeit, Empfänger, Rollenfilter, Bau des
 * Actors — ist für jeden OIDC-Anbieter gleich und steht deshalb **nicht** hier,
 * sondern in `anmeldung.ts`. Wanderte es in die Ausprägung, hätte jeder neue
 * Anbieter die Gelegenheit, es anders zu machen.
 */

import type { Anspruch } from "./anspruch.js";
import { alsText } from "./anspruch.js";
import { ohnePfad } from "./rollen.js";

/**
 * Wie weit eine zentrale Abmeldung oder Sperrung reicht.
 *
 * `backchannel` — der Anbieter meldet die Abmeldung direkt an die Anwendung;
 * sie wirkt sofort. `sitzungsdauer` — er kann das nicht, und die eigene
 * Sitzung der Anwendung läuft bis zu ihrem Ablauf weiter.
 *
 * Der Wert gehört in den Vertrag, weil er den Unterschied zwischen "sofort
 * ausgesperrt" und "bis zu zwölf Stunden weiter drin" ausmacht. In
 * `POC-IAM-001` waren es vor der Korrektur **21 Tage**.
 */
export type Abmeldereichweite = "backchannel" | "sitzungsdauer";

export interface IdentitaetsAnbieter {
  readonly name: string;
  /** Erwarteter `iss`. Vertrauensanker jeder Prüfung — nie aus dem Token. */
  readonly aussteller: string;
  /** Für wen die Token bestimmt sind; wird gegen `aud` geprüft. */
  readonly empfaenger: string;
  /** Die Gruppennamen aus den Ansprüchen, normalisiert. */
  gruppenAus(anspruch: Anspruch): readonly string[];
  /**
   * Ob dieses Token zu einem Dienstkonto gehört.
   *
   * OIDC kennt kein Feld dafür, jeder Anbieter markiert es anders — deshalb
   * hier und nicht in der gemeinsamen Prüfung. `10` §10.9 verlangt, dass ein
   * Dienstkonto sich nicht interaktiv anmelden kann; ohne diese Auskunft
   * lässt sich das nicht durchsetzen.
   */
  istDienstkonto(anspruch: Anspruch): boolean;
  readonly abmeldung: Abmeldereichweite;
}

/** Keycloak, so wie `infra/keycloak/` ihn aufsetzt. */
export function keycloakAnbieter(einstellung: {
  readonly aussteller: string;
  readonly empfaenger: string;
}): IdentitaetsAnbieter {
  return {
    name: "keycloak",
    aussteller: einstellung.aussteller,
    empfaenger: einstellung.empfaenger,
    gruppenAus(anspruch) {
      const roh = anspruch["groups"];
      if (!Array.isArray(roh)) return [];
      return roh
        .filter((g): g is string => typeof g === "string")
        .map(ohnePfad);
    },
    // Keycloaks Namenskonvention für Dienstkonten. Sie ist nicht
    // konfigurierbar und taucht in `preferred_username` auf.
    istDienstkonto(anspruch) {
      const nutzer = alsText(anspruch["preferred_username"]);
      return nutzer !== undefined && nutzer.startsWith("service-account-");
    },
    // Belegt in POC-IAM-001: Keycloak meldet die Abmeldung an Nextcloud, und
    // die Sitzung dort endet sofort. Dass Vikunja und Paperless es nicht
    // können, ist eine Eigenschaft **dieser Anwendungen**, nicht des
    // Anbieters — sie steht deshalb in `infra/identity/rollen.yaml`.
    abmeldung: "backchannel",
  };
}

/**
 * Ein beliebiger anderer OIDC-Anbieter.
 *
 * Diese Ausprägung existiert nicht als Vorrat, sondern als **Nachweis**: Die
 * Abnahme von `IAM-001` verlangt einen Providerwechsel-Test auf Vertragsebene,
 * und der ist nur etwas wert, wenn dieselbe Prüfsuite gegen eine zweite,
 * unabhängig geschriebene Ausprägung läuft. Ein Vertrag, den nur eine
 * Ausprägung erfüllt, ist eine Schnittstelle zu sich selbst.
 *
 * Die Vorgaben sind die vorsichtigen: Anspruchsname `groups`, keine Pfadform,
 * **keine** Dienstkontoerkennung und `sitzungsdauer` als Reichweite. Wer einen
 * Anbieter anbindet, der mehr kann, sagt es ausdrücklich — statt dass die
 * Vorgabe mehr verspricht, als der Anbieter hält.
 */
export function generischerAnbieter(einstellung: {
  readonly name: string;
  readonly aussteller: string;
  readonly empfaenger: string;
  readonly gruppenAnspruch?: string;
  readonly pfadform?: boolean;
  readonly dienstkonto?: (anspruch: Anspruch) => boolean;
  readonly abmeldung?: Abmeldereichweite;
}): IdentitaetsAnbieter {
  const anspruchsname = einstellung.gruppenAnspruch ?? "groups";
  const pfadform = einstellung.pfadform ?? false;
  const dienstkonto = einstellung.dienstkonto;
  return {
    name: einstellung.name,
    aussteller: einstellung.aussteller,
    empfaenger: einstellung.empfaenger,
    gruppenAus(anspruch) {
      const roh = anspruch[anspruchsname];
      if (!Array.isArray(roh)) return [];
      const namen = roh.filter((g): g is string => typeof g === "string");
      return pfadform ? namen.map(ohnePfad) : namen;
    },
    istDienstkonto(anspruch) {
      return dienstkonto ? dienstkonto(anspruch) : false;
    },
    abmeldung: einstellung.abmeldung ?? "sitzungsdauer",
  };
}
